/*
 * DOM lookups shared by the drop animation (useTalentReviewDropStyle) and the
 * insertion tracking (talentReviewInsertion): where the avatars of a droppable
 * are, as laid out.
 */

export type Point = { x: number; y: number };

/**
 * How far an element sits from where it was laid out. dnd writes that
 * displacement as an inline `translate(Xpx, Ypx)`, so reading it back and
 * subtracting it gives the undisplaced position. Anything else — no transform,
 * or one without a plain two-value `translate` — counts as unshifted.
 * @param transform the element's inline `transform` value
 * @returns the shift in pixels, zero when there is none to read
 */
export const parseTranslate = (transform: string | null | undefined): Point => {
	const match = transform?.match(/translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)/);
	return match ? { x: parseFloat(match[1]), y: parseFloat(match[2]) } : { x: 0, y: 0 };
};

/**
 * The element the avatars of a droppable are laid out in: the droppable itself,
 * or the descendant marked `data-drop-area` when the droppable also holds other
 * content (the pool has a title above its avatars).
 * @param droppableId the droppable id
 * @returns the area, or undefined when the droppable is not on the page
 */
export const getDropArea = (droppableId: string) => {
	const container = document.querySelector<HTMLElement>(`[data-rbd-droppable-id="${droppableId}"]`);
	return container?.querySelector<HTMLElement>('[data-drop-area]') ?? container ?? undefined;
};

/**
 * The avatars in a drop area that take part in its flow, in DOM order. The
 * dragged avatar (fixed), a focused (lifted) one and a leaving one (collapsed to
 * no width) sit outside the flow and are skipped, as is dnd's placeholder.
 * @param area the drop area
 * @returns the avatar elements
 */
export const getAvatars = (area: HTMLElement) =>
	Array.from(area.querySelectorAll<HTMLElement>('[data-rbd-draggable-id]')).filter((element) => {
		const style = getComputedStyle(element);
		return (
			style.position !== 'fixed' &&
			style.visibility !== 'hidden' &&
			(!element.parentElement || getComputedStyle(element.parentElement).position !== 'absolute')
		);
	});

/**
 * An avatar's box without dnd's displacement: while dragging over a list, dnd
 * translates the avatars after the pointer to make room, but the person mounts
 * in the undisplaced layout.
 * @param element the avatar element
 * @returns its viewport box as laid out
 */
export const getLaidOutRect = (element: HTMLElement) => {
	const rect = element.getBoundingClientRect();
	const shift = parseTranslate(element.style.transform);
	return {
		left: rect.left - shift.x,
		right: rect.right - shift.x,
		top: rect.top - shift.y,
		bottom: rect.bottom - shift.y,
	};
};

/**
 * The centre of the avatar being dragged, which is also the point dnd uses to
 * decide which droppable it is over.
 * @param draggableId the dragged person
 * @returns the centre, or undefined when the avatar is not on the page
 */
export const getDraggedCenter = (draggableId: string): Point | undefined => {
	const element = document.querySelector<HTMLElement>(`[data-rbd-draggable-id="${draggableId}"]`);
	const rect = element?.getBoundingClientRect();
	return rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : undefined;
};
